import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Form, Modal, Select, message, Popconfirm, Tag, Row, Col, InputNumber, Card, Statistic } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, EyeOutlined, HomeOutlined } from '@ant-design/icons';
import { warehouseAPI } from '../../services/api';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const WarehouseList = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({ type: '', status: '' });
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [warehouseDetail, setWarehouseDetail] = useState(null);
  const [form] = Form.useForm();

  // 仓库类型映射
  const typeMap = {
    'main': { text: '主仓库', color: 'blue' },
    'branch': { text: '分仓库', color: 'green' },
    'transit': { text: '中转仓', color: 'orange' },
    'defective': { text: '不良品仓', color: 'red' }
  };

  // 状态映射
  const statusMap = {
    'active': { text: '启用', color: 'green' },
    'inactive': { text: '停用', color: 'default' }
  };

  const columns = [
    {
      title: '仓库编码',
      dataIndex: 'code',
      key: 'code',
      width: 120
    },
    {
      title: '仓库名称',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: '仓库类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (text) => {
        const type = typeMap[text] || { text: text, color: 'default' };
        return <Tag color={type.color}>{type.text}</Tag>;
      }
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      width: 200,
      ellipsis: true
    },
    {
      title: '面积(m²)',
      dataIndex: 'area',
      key: 'area',
      width: 100,
      render: (text) => text ? `${parseFloat(text).toLocaleString()} m²` : '-'
    },
    {
      title: '容量(m³)',
      dataIndex: 'capacity',
      key: 'capacity',
      width: 100,
      render: (text) => text ? `${parseFloat(text).toLocaleString()} m³` : '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (text) => {
        const status = statusMap[text] || { text: text, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个仓库吗?"
            description="删除后将无法恢复"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchWarehouses();
  }, [pagination.current, pagination.pageSize, searchText, filters]);

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const response = await warehouseAPI.getList({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        ...filters
      });

      setWarehouses(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('获取仓库列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleEdit = (record) => {
    setEditingWarehouse(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await warehouseAPI.getInventoryStats(record.id);
      setWarehouseDetail(response);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('获取仓库详情失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await warehouseAPI.delete(id);
      message.success('删除成功');
      fetchWarehouses();
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const handleAdd = () => {
    setEditingWarehouse(null);
    form.resetFields();
    form.setFieldsValue({
      type: 'main',
      status: 'active'
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingWarehouse) {
        await warehouseAPI.update(editingWarehouse.id, values);
        message.success('更新成功');
      } else {
        await warehouseAPI.create(values);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchWarehouses();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '操作失败');
      }
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setSearchText('');
    setFilters({ type: '', status: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>仓库管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          新增仓库
        </Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Search
              placeholder="搜索仓库编码/名称"
              onSearch={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="仓库类型"
              style={{ width: 120 }}
              allowClear
              value={filters.type || undefined}
              onChange={(value) => handleFilterChange('type', value)}
            >
              <Option value="main">主仓库</Option>
              <Option value="branch">分仓库</Option>
              <Option value="transit">中转仓</Option>
              <Option value="defective">不良品仓</Option>
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="状态"
              style={{ width: 100 }}
              allowClear
              value={filters.status || undefined}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="active">启用</Option>
              <Option value="inactive">停用</Option>
            </Select>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={warehouses}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`
        }}
        onChange={handleTableChange}
      />

      {/* 新增/编辑仓库弹窗 */}
      <Modal
        title={editingWarehouse ? '编辑仓库' : '新增仓库'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="仓库编码"
                rules={[{ required: true, message: '请输入仓库编码' }]}
              >
                <Input placeholder="请输入仓库编码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="仓库名称"
                rules={[{ required: true, message: '请输入仓库名称' }]}
              >
                <Input placeholder="请输入仓库名称" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="type" label="仓库类型">
                <Select placeholder="请选择仓库类型">
                  <Option value="main">主仓库</Option>
                  <Option value="branch">分仓库</Option>
                  <Option value="transit">中转仓</Option>
                  <Option value="defective">不良品仓</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="状态">
                <Select placeholder="请选择状态">
                  <Option value="active">启用</Option>
                  <Option value="inactive">停用</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="address" label="仓库地址">
            <Input placeholder="请输入仓库地址" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="area" label="仓库面积">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  addonAfter="m²"
                  placeholder="请输入面积"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="capacity" label="仓库容量">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  addonAfter="m³"
                  placeholder="请输入容量"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 仓库详情弹窗 */}
      <Modal
        title={
          <Space>
            <HomeOutlined />
            仓库详情 - {warehouseDetail?.warehouse?.name}
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {warehouseDetail && (
          <div>
            <Card style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <p><strong>仓库编码：</strong>{warehouseDetail.warehouse.code}</p>
                  <p><strong>仓库类型：</strong>{typeMap[warehouseDetail.warehouse.type]?.text}</p>
                  <p><strong>仓库地址：</strong>{warehouseDetail.warehouse.address || '-'}</p>
                </Col>
                <Col span={12}>
                  <p><strong>仓库面积：</strong>{warehouseDetail.warehouse.area ? `${warehouseDetail.warehouse.area} m²` : '-'}</p>
                  <p><strong>仓库容量：</strong>{warehouseDetail.warehouse.capacity ? `${warehouseDetail.warehouse.capacity} m³` : '-'}</p>
                  <p><strong>状态：</strong>
                    <Tag color={statusMap[warehouseDetail.warehouse.status]?.color}>
                      {statusMap[warehouseDetail.warehouse.status]?.text}
                    </Tag>
                  </p>
                </Col>
              </Row>
            </Card>

            <Card title="库存统计">
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="商品种类"
                    value={warehouseDetail.stats.productCount || 0}
                    suffix="种"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="库存总量"
                    value={warehouseDetail.stats.totalQuantity || 0}
                    suffix="件"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="锁定数量"
                    value={warehouseDetail.stats.totalLocked || 0}
                    suffix="件"
                    valueStyle={{ color: warehouseDetail.stats.totalLocked > 0 ? '#faad14' : undefined }}
                  />
                </Col>
              </Row>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WarehouseList;
